import * as _ from "lodash";
import { ILease } from "./PA.UtilsIF";
import { GenericLease } from "./GenericLease";
import { LeaseRegistry } from "./LeaseRegistry";
import * as AsyncLock from "async-lock";
import { PASearch } from "../PA.Core/PASearch";
import { DateUtils } from "../PA.Core/Basic/Dates";
import { Customer_Hierarchy_Max_Depth } from "../PA.MD.Core/MD.Constants";
import { CustomerHierarchyService } from "../PA.MD.Services/CustomerHierarchyService";
import { IDateRange } from "../PA.Core/PA.Core.IF.Basic";

class CustomerHierarchyLink {
    relWbID: number;
    childWbID: number;
    parentWbID: number;
    childOrgLevel: string;
    parentOrgLevel: string;
    effectiveStartDt: Date;
    effectiveEndDt: Date;
    sourceID?: string;
}

function copyLink(anOrigLink: CustomerHierarchyLink, aNewLink: CustomerHierarchyLink) {
    aNewLink.relWbID = anOrigLink.relWbID;
    aNewLink.parentWbID = anOrigLink.parentWbID;
    aNewLink.parentOrgLevel = anOrigLink.parentOrgLevel;
    aNewLink.childWbID = anOrigLink.childWbID;
    aNewLink.childOrgLevel = anOrigLink.childOrgLevel;
    aNewLink.effectiveStartDt = anOrigLink.effectiveStartDt;
    aNewLink.effectiveEndDt = anOrigLink.effectiveEndDt;
    aNewLink.sourceID = anOrigLink.sourceID;
}

export class LegacyCustomerHierarchyContext {
    private readonly custHierWbID: number;
    private dctLinksByChildWbID: { [id: number]: CustomerHierarchyLink[] } = {};
    private dctLinksByParentWbID: { [id: number]: CustomerHierarchyLink[] } = {};
    private dctParentByChildWbOrgLevel: { [id: string]: number } = {};
    private dctLinksByPrntCustWbIDOrgLevel: { [id: string]: number[] } = {};
    private dctLock: any = new AsyncLock();
    private bIsLoaded: boolean = false;

    private _lease: ILease;

    constructor(custHWbID: number) {
        this.custHierWbID = custHWbID;
        this.createLease();
    }

    getWBIdsByCustomerType(customerType: string): Promise<number[]> {
        const self = this;
        return new Promise<number[]>((resolve, error) => {
            self.dctLock.acquire("global", (done: any) => {
                self.checkLease()
                    .then((ignore) => {
                        done(null);
                        resolve(self.getNodes(customerType));
                        return;
                    })
                    .catch((err) => {
                        done(null);
                        error(err);
                    });
            });
        });
    }

    getParentWbID(
        custWbID: number,
        effectiveStartDt: Date,
        effectiveEndDt: Date,
        customerType?: string,
        depthLimit?: number,
    ): Promise<number> {
        const self = this;
        return new Promise<number>((resolve, error) => {
            customerType = customerType || "";
            let dctKey: string =
                custWbID.toString() +
                customerType +
                DateUtils.convertToSAPBW(effectiveStartDt) +
                DateUtils.convertToSAPBW(effectiveEndDt);

            depthLimit = depthLimit || Customer_Hierarchy_Max_Depth;
            if (depthLimit < 0) {
                error("Customer hierarchy search depth must be positive");
            }

            self.dctLock.acquire("global", (done: any) => {
                self.checkLease()
                    .then((ignore) => {
                        if (dctKey in self.dctParentByChildWbOrgLevel) {
                            done(null, self.dctParentByChildWbOrgLevel[dctKey]);
                            resolve(self.dctParentByChildWbOrgLevel[dctKey]);
                            return;
                        }

                        let parentLink: CustomerHierarchyLink = new CustomerHierarchyLink();

                        self.climbToParentCustomerType(
                            custWbID,
                            effectiveStartDt,
                            effectiveEndDt,
                            customerType,
                            parentLink,
                            depthLimit,
                        )
                            .then((ignore) => {
                                if (!_.isUndefined(parentLink.relWbID)) {
                                    self.dctParentByChildWbOrgLevel[dctKey] = parentLink.parentWbID;
                                } else {
                                    self.dctParentByChildWbOrgLevel[dctKey] = undefined;
                                }

                                done(null, self.dctParentByChildWbOrgLevel[dctKey]);
                                resolve(self.dctParentByChildWbOrgLevel[dctKey]);
                                return;
                            })
                            .catch((err) => {
                                error(err);
                            });
                    })
                    .catch((err) => {
                        error(err);
                    });
            });
        });
    }

    getAllCustParents(wbCustID: number): Promise<{ wbID: number; dateRange: IDateRange }[]> {
        const self = this;
        return new Promise<{ wbID: number; dateRange: IDateRange }[]>((resolve, error) => {
            self.checkLease()
                .then((ignore) => {
                    return self.getParents(wbCustID);
                })
                .then((links: CustomerHierarchyLink[]) => {
                    let retVal: { wbID: number; dateRange: IDateRange }[] = _.map(links, (aLink) => {
                        return {
                            wbID: aLink.parentWbID,
                            dateRange: { StartDt: aLink.effectiveStartDt, EndDt: aLink.effectiveEndDt },
                        };
                    });
                    resolve(retVal);
                    return;
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    getChildren(
        custWbID: number,
        effectiveStartDt: Date,
        effectiveEndDt: Date,
        customerType?: string,
        depthLimit?: number,
    ): Promise<number[]> {
        const self = this;
        return new Promise<number[]>((resolve, error) => {
            customerType = customerType || "";
            depthLimit = depthLimit || Customer_Hierarchy_Max_Depth;

            if (depthLimit < 0) {
                error("Customer hierarchy search depth must be positive");
            }

            let dctKey: string =
                custWbID.toString() +
                customerType +
                DateUtils.convertToSAPBW(effectiveStartDt) +
                DateUtils.convertToSAPBW(effectiveEndDt);
            let links: CustomerHierarchyLink[] = [];

            self.dctLock.acquire("global", (done: any) => {
                self.checkLease()
                    .then((ignore) => {
                        if (dctKey in self.dctLinksByPrntCustWbIDOrgLevel) {
                            done(null, self.dctLinksByPrntCustWbIDOrgLevel[dctKey]);
                            resolve(self.dctLinksByPrntCustWbIDOrgLevel[dctKey]);
                            return;
                        }

                        this.getChildrenByType(
                            custWbID,
                            effectiveStartDt,
                            effectiveEndDt,
                            customerType,
                            depthLimit,
                            links,
                        )
                            .then((ignore) => {
                                self.dctLinksByPrntCustWbIDOrgLevel[dctKey] = _.uniq(
                                    _.map(links, (aLink: CustomerHierarchyLink) => {
                                        return aLink.childWbID;
                                    }),
                                );
                                done(null, self.dctLinksByPrntCustWbIDOrgLevel[dctKey]);
                                resolve(self.dctLinksByPrntCustWbIDOrgLevel[dctKey]);
                                return;
                            })
                            .catch((err) => {
                                error(err);
                            });
                    })
                    .catch((err) => {
                        error(err);
                    });
            });
        });
    }

    isCustWbIdInHierarchy(custWbId: number): Promise<boolean> {
        //return (custWbId in this.dctLinksByChildWbID || custWbId in this.dctLinksByParentWbID)
        const self = this;
        return new Promise<boolean>((resolve, error) => {
            self.dctLock.acquire("global", (done: any) => {
                self.checkLease()
                    .then((ignore) => {
                        done(null, custWbId);
                        resolve(custWbId in self.dctLinksByParentWbID || custWbId in self.dctLinksByChildWbID);
                        return;
                    })
                    .catch((err) => {
                        error(err);
                    });
            });
        });
    }

    //<editor-fold desc="Helper methods">

    private getNodes(customerLevel: string): number[] {
        let dctResults: { [id: number]: number } = {};

        _.forEach(_.values(this.dctLinksByParentWbID), (anArray) => {
            _.forEach(anArray, (aLink: CustomerHierarchyLink) => {
                if (aLink.childOrgLevel == customerLevel) {
                    dctResults[aLink.childWbID] = aLink.childWbID;
                }
                if (aLink.parentOrgLevel == customerLevel) {
                    dctResults[aLink.parentWbID] = aLink.parentWbID;
                }
            });
        });

        return _.values(dctResults);
    }

    private async getParents(custWbID: number): Promise<CustomerHierarchyLink[]> {
        const self = this;
        return new Promise<CustomerHierarchyLink[]>((resolve, error) => {
            self.dctLock.acquire("global", (done: any) => {
                if (custWbID in self.dctLinksByChildWbID) {
                    done(null, self.dctLinksByChildWbID[custWbID]);
                    resolve(self.dctLinksByChildWbID[custWbID]);
                    return;
                } else if (self.bIsLoaded && !(custWbID in self.dctLinksByChildWbID)) {
                    done(null, self.dctLinksByChildWbID[custWbID]);
                    resolve([]);
                    return;
                }

                self.load()
                    .then((ignore) => {
                        done(null, self.dctLinksByChildWbID[custWbID]);
                        resolve(self.dctLinksByChildWbID[custWbID]);
                        return;
                    })
                    .catch((err) => {
                        error(err);
                    });
            });
        });
    }

    private async climbToParentCustomerType(
        custWbID: number,
        effectiveStartDt: Date,
        effectiveEndDt: Date,
        customerType: string,
        retVal: CustomerHierarchyLink,
        depthLimit: number,
    ) {
        let currentPrntWbID: number;
        let currentPrntType: string;
        let currentLink: CustomerHierarchyLink;
        let remainingDepth: number = depthLimit;
        currentPrntWbID = custWbID;
        currentPrntType = "Unknown";

        //first check to see if we're looking for the same level product as the custWbID passed in
        currentLink = _.first(
            _.filter(
                this.dctLinksByParentWbID[custWbID],
                (aLink: CustomerHierarchyLink) =>
                    aLink.parentWbID == custWbID &&
                    DateUtils.RangeDatesOverlap(
                        aLink.effectiveStartDt,
                        aLink.effectiveEndDt,
                        effectiveStartDt,
                        effectiveEndDt,
                    ),
            ),
        );
        if (!_.isUndefined(currentLink) && currentLink.parentOrgLevel == customerType) {
            copyLink(currentLink, retVal);
            return;
        }

        while (customerType != currentPrntType) {
            currentLink = _.first(
                _.filter(this.dctLinksByChildWbID[currentPrntWbID], (aLink: CustomerHierarchyLink) => {
                    return (
                        DateUtils.RangeDatesOverlap(
                            aLink.effectiveStartDt,
                            aLink.effectiveEndDt,
                            effectiveStartDt,
                            effectiveEndDt,
                        ) && aLink.childWbID !== aLink.parentWbID
                    );
                }),
            );

            if (!_.isUndefined(currentLink)) {
                if (customerType == "" || currentLink.parentOrgLevel == customerType) {
                    copyLink(currentLink, retVal);
                    break;
                }

                currentPrntWbID = currentLink.parentWbID;
                currentPrntType = currentLink.parentOrgLevel;

                remainingDepth -= 1;
                if (remainingDepth < 0) {
                    break;
                }
            } else {
                break;
            }
        }
    }

    private async getChildrenByType(
        custWbID: number,
        effectiveStartDt: Date,
        effectiveEndDt: Date,
        custType: string,
        depthLimit: number,
        retVal: CustomerHierarchyLink[],
    ): Promise<void> {
        if (depthLimit < 0) {
            return;
        }

        let newDepth: number = depthLimit - 1;
        let currentLinkList: CustomerHierarchyLink[] = this.dctLinksByParentWbID[custWbID];

        if (_.isUndefined(currentLinkList)) {
            return;
        }

        for (let aLink of currentLinkList) {
            if (
                !DateUtils.RangeDatesOverlap(
                    aLink.effectiveStartDt,
                    aLink.effectiveEndDt,
                    effectiveStartDt,
                    effectiveEndDt,
                )
            ) {
                continue;
            }
            if (aLink.childOrgLevel == custType || custType == "") {
                retVal.push(aLink);
            }
            await this.getChildrenByType(aLink.childWbID, effectiveStartDt, effectiveEndDt, custType, newDepth, retVal);
        }
    }

    async reset(): Promise<void> {
        this.bIsLoaded = false;
        this.dctLinksByChildWbID = {};
        this.dctLinksByParentWbID = {};
        this.dctParentByChildWbOrgLevel = {};
        this.dctLinksByPrntCustWbIDOrgLevel = {};
        this._lease.forceExpire();
    }

    private load(): Promise<void> {
        const self = this;
        return new Promise<void>((resolve, error) => {
            PASearch.find(
                "vWB_CustR_CustProps",
                { DELETED_IND: { neq: "1" }, WB_CUSTH_ID: self.custHierWbID },
                { DELETED_IND: { type: "string" }, WB_CUSTH_ID: { type: "number" } },
                ["EFFECTIVE_START_DT"],
                0,
                [],
            )
                .then((results: any[]) => {
                    _.forEach(results, (aResult: any) => {
                        let linkObject: CustomerHierarchyLink = new CustomerHierarchyLink();
                        linkObject.relWbID = aResult.WB_CUSTR_ID;
                        linkObject.childWbID = aResult.WB_CUST_ID;
                        linkObject.childOrgLevel = aResult.LEVEL_ID_CHILD;
                        linkObject.parentWbID = aResult.WB_CUST_ID_PRNT;
                        linkObject.parentOrgLevel = aResult.LEVEL_ID_PRNT;
                        linkObject.effectiveStartDt = DateUtils.convertFromSAPBW(aResult.EFFECTIVE_START_DT);
                        linkObject.effectiveEndDt = DateUtils.convertFromSAPBW(aResult.EFFECTIVE_END_DT);
                        linkObject.sourceID = aResult.SOURCE_ID_PRNT;
                        self.cacheLinkObject(linkObject);
                    });
                    self._lease.resetLease();
                    self.bIsLoaded = true;
                    resolve();
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    private cacheLinkObject(aRelLink: CustomerHierarchyLink) {
        if (!(aRelLink.childWbID in this.dctLinksByChildWbID)) {
            this.dctLinksByChildWbID[aRelLink.childWbID] = [];
        }
        this.dctLinksByChildWbID[aRelLink.childWbID].push(aRelLink);

        if (!(aRelLink.parentWbID in this.dctLinksByParentWbID)) {
            this.dctLinksByParentWbID[aRelLink.parentWbID] = [];
        }
        this.dctLinksByParentWbID[aRelLink.parentWbID].push(aRelLink);
    }

    //</editor-fold>

    //<editor-fold desc="Leasing">

    private createLease() {
        this._lease = new GenericLease(LeaseRegistry.Lease_Subject_Customer_Hierarchy);
        this._lease.addClasses(CustomerHierarchyService.getLeaseClasses(this.custHierWbID));
    }

    private async checkLease(): Promise<void> {
        if (!this.bIsLoaded) {
            this._lease.forceExpire();
        }

        if (await this._lease.hasDataChanged()) {
            await this.reset();
            await this.load();
        }
    }

    //</editor-fold>
}
